import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Sheet from "components/sheet/Sheet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowCircleRight,
  faCheck,
  faCircleInfo,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { ICustomer } from "redux/reducers/models/customer";
import { TMergeDataCategory, TMergeDataKey } from "./loadMergeAccountData";
import "./mergeCustomers.scss";

const MERGE_REVIEW_GRID_COLUMNS: TMergeDataKey[][] = [
  ["bookings", "reservations", "memberships", "notes"],
  ["tickets", "house_accounts", "gift_cards", "orders"],
  ["credit_books", "loyalty_points", "playing_partners"],
];

function groupTransferredByColumn(categories: TMergeDataCategory[]): TMergeDataCategory[][] {
  const byKey = new Map(categories.map(category => [category.key, category]));

  return MERGE_REVIEW_GRID_COLUMNS.map(keys =>
    keys.map(key => byKey.get(key)).filter((category): category is TMergeDataCategory => !!category),
  ).filter(column => column.length > 0);
}

function getCustomerSubtitle(customer: ICustomer | null): string {
  if (!customer) {
    return "—";
  }
  const type = customer.customer_type?.trim() || "";
  const memberCode = customer.member_code?.trim() || "";
  if (type && memberCode) {
    return `${type} • ${memberCode}`;
  }
  return type || memberCode || "—";
}

export interface IMergeReviewProps {
  open: boolean;
  closeCustomer: ICustomer | null;
  keepCustomer: ICustomer | null;
  categories: TMergeDataCategory[];
  loading?: boolean;
  onCancel: () => void;
  onBack: () => void;
  onConfirm: () => void;
}

export default function MergeReview(props: IMergeReviewProps) {
  const { t } = useTranslation();
  const { open, closeCustomer, keepCustomer, categories, loading, onCancel, onBack, onConfirm } = props;

  const transferred = useMemo(() => categories.filter(category => category.selected), [categories]);
  const notTransferred = useMemo(() => categories.filter(category => !category.selected), [categories]);
  const transferredColumns = useMemo(() => groupTransferredByColumn(transferred), [transferred]);

  const closeName = closeCustomer?.full_name || "—";
  const keepName = keepCustomer?.full_name || "—";

  return (
    <Sheet
      title={t("Review merge")}
      supportText={t("{{closeName}}'s account will be permanently closed and merged into {{keepName}}'s account.", {
        closeName,
        keepName,
      })}
      open={open}
      size="modal"
      closable
      stacked
      onCancel={onBack}
      onClose={onCancel}
      onOk={onConfirm}
      okText={t("Merge accounts")}
      cancelText={t("Go back")}
      okLoading={loading}
      height="flexible"
    >
      <div className="merge-customers-review">
        <div className="merge-customers-review__section">
          <p className="merge-customers-review__section-title">{t("Merge summary")}</p>
          <div className="merge-customers-review__summary">
            <div className="merge-customers-review__party">
              <span className="merge-customers-review__party-name">{closeName}</span>
              <span className="merge-customers-review__party-subtitle">{getCustomerSubtitle(closeCustomer)}</span>
            </div>
            <div className="merge-customers-review__arrow" aria-hidden>
              <FontAwesomeIcon icon={faArrowCircleRight} />
            </div>
            <div className="merge-customers-review__party">
              <span className="merge-customers-review__party-name">{keepName}</span>
              <span className="merge-customers-review__party-subtitle">{getCustomerSubtitle(keepCustomer)}</span>
            </div>
          </div>
        </div>

        <div className="merge-customers-review__section">
          <p className="merge-customers-review__list-title">
            {t("Will be transferred")} <span>({transferred.length})</span>
          </p>
          <div className="merge-customers-review__box merge-customers-review__box--success">
            {transferred.length ? (
              <div className="merge-customers-review__grid">
                {transferredColumns.map((column, columnIndex) => (
                  <React.Fragment key={column.map(category => category.key).join("-")}>
                    {columnIndex > 0 ? <div className="merge-customers-review__grid-divider" aria-hidden /> : null}
                    <ul className="merge-customers-review__grid-column">
                      {column.map(category => (
                        <li key={category.key} className="merge-customers-review__grid-item">
                          <FontAwesomeIcon
                            icon={faCheck}
                            className="merge-customers-review__icon--success"
                            aria-hidden
                          />
                          <span>{t(category.label)}</span>
                        </li>
                      ))}
                    </ul>
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <p className="merge-customers-review__empty">{t("No data selected")}</p>
            )}
          </div>
        </div>

        {notTransferred.length ? (
          <div className="merge-customers-review__section">
            <p className="merge-customers-review__list-title">
              {t("Will not be transferred")} <span>({notTransferred.length})</span>
            </p>
            <div className="merge-customers-review__box merge-customers-review__box--error">
              <ul className="merge-customers-review__excluded">
                {notTransferred.map(category => (
                  <li key={category.key} className="merge-customers-review__grid-item">
                    <FontAwesomeIcon icon={faXmark} className="merge-customers-review__icon--error" aria-hidden />
                    <span>{t(category.label)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : null}

        <div className="merge-customers-review__alert" role="status">
          <FontAwesomeIcon icon={faCircleInfo} className="merge-customers-review__alert-icon" aria-hidden />
          <p className="merge-customers-review__alert-text">{t("This action cannot be undone.")}</p>
        </div>
      </div>
    </Sheet>
  );
}
